import { DivergenceStatusEnum } from "@/enum/divergence-status";
import { DESCONTO_EM_FOLHA_POR_OPERADOR } from "@/constants/justifications.id";

import { isPayrollDiscount, PayrollDiscountCandidate } from "./is-payroll-discount";

export const BATCH_SKIP_REASONS = {
  NOT_FOUND: "NOT_FOUND",
  CLOSED_PERIOD: "CLOSED_PERIOD",
  ALREADY_DECIDED: "ALREADY_DECIDED",
  NOT_IN_QUEUE: "NOT_IN_QUEUE",
  OPERATOR_UNRESOLVED: "OPERATOR_UNRESOLVED",
  NON_NEGATIVE_AMOUNT: "NON_NEGATIVE_AMOUNT",
  INSTALLMENTS_REQUIRED: "INSTALLMENTS_REQUIRED",
} as const;

const CENTS_IN_UNIT: number = 100;

const NO_AMOUNT: number = 0;

const isKnownNonNegative = (amount?: string | number | null): boolean => {
  if (amount === null || amount === undefined || amount === "") return false;

  return Math.round(Number(amount) * CENTS_IN_UNIT) >= NO_AMOUNT;
};

export type BatchSkipReason =
  (typeof BATCH_SKIP_REASONS)[keyof typeof BATCH_SKIP_REASONS];

export interface PayrollAmountCandidate extends PayrollDiscountCandidate {
  amount?: string | number | null;
}

export interface BatchCandidate extends PayrollAmountCandidate {
  id: number;
  name?: string | null;
  status?: DivergenceStatusEnum | null;

  closed_period?: boolean;
}

export interface BatchSkipped {
  item: BatchCandidate;
  reason: BatchSkipReason;
}

export interface BatchPreflight {
  valid: BatchCandidate[];
  skipped: BatchSkipped[];
  payrollDiscountCount: number;
}

const DECIDABLE_STATUSES: readonly DivergenceStatusEnum[] = [
  DivergenceStatusEnum.PENDING,
  DivergenceStatusEnum.ACTIVE,
];

const belongsToQueue = (candidate: BatchCandidate): boolean =>
  candidate.is_loss === true ||
  candidate.justification_id === DESCONTO_EM_FOLHA_POR_OPERADOR;

const skipReasonOf = (
  candidate: BatchCandidate,
  requestedStatus: DivergenceStatusEnum
): BatchSkipReason | null => {
  if (candidate.closed_period) return BATCH_SKIP_REASONS.CLOSED_PERIOD;

  if (!DECIDABLE_STATUSES.includes(candidate.status as DivergenceStatusEnum)) {
    return BATCH_SKIP_REASONS.ALREADY_DECIDED;
  }

  if (!belongsToQueue(candidate)) return BATCH_SKIP_REASONS.NOT_IN_QUEUE;

  if (blocksPayrollDiscount(candidate, requestedStatus)) {
    return BATCH_SKIP_REASONS.NON_NEGATIVE_AMOUNT;
  }

  return null;
};

const goesToPayroll = (
  candidate: PayrollAmountCandidate,
  requestedStatus: DivergenceStatusEnum
): boolean =>
  requestedStatus === DivergenceStatusEnum.APPROVED &&
  isPayrollDiscount(candidate);

export const blocksPayrollDiscount = (
  candidate: PayrollAmountCandidate,
  requestedStatus: DivergenceStatusEnum
): boolean =>
  goesToPayroll(candidate, requestedStatus) &&
  isKnownNonNegative(candidate.amount);

export const buildBatchPreflight = (
  candidates: BatchCandidate[],
  requestedStatus: DivergenceStatusEnum
): BatchPreflight => {
  const valid: BatchCandidate[] = [];
  const skipped: BatchSkipped[] = [];

  candidates.forEach((candidate: BatchCandidate): void => {
    const reason: BatchSkipReason | null = skipReasonOf(
      candidate,
      requestedStatus
    );

    if (!reason) {
      valid.push(candidate);
      return;
    }

    skipped.push({ item: candidate, reason });
  });

  return {
    valid,
    skipped,
    payrollDiscountCount: valid.filter((candidate: BatchCandidate): boolean =>
      goesToPayroll(candidate, requestedStatus)
    ).length,
  };
};
